/**
 * Success Display Component
 * 
 * Displays the success message after successful subscription payment.
 * Includes a button to access the Stripe customer portal.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { useSubscription } from '../../subscription';
import StripeService from '../services/StripeService';
import { trackEvent } from '../../analytics/services/analyticsService';
import Button from '../../ui/components/Button';
import './SuccessDisplay.css';

/**
 * SuccessDisplay - Success view after payment
 * @param {Object} props - Component props
 * @param {string} props.sessionId - Stripe checkout session ID
 * @returns {JSX.Element} Success display component
 */
const SuccessDisplay = ({ sessionId }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const subscription = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextPaymentDate, setNextPaymentDate] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const subscriptionActivatedEventFired = useRef(false);

  // Fire subscription_activated once when success screen is shown (so GTM always receives it)
  useEffect(() => {
    if (sessionId && !subscriptionActivatedEventFired.current) {
      subscriptionActivatedEventFired.current = true;
      trackEvent('subscription_activated', {
        transaction_id: sessionId,
        currency: 'BGN',
        value: 35.00
      });
    }
  }, [sessionId]);

  /**
   * Refresh subscription status after successful payment
   */
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (sessionId && subscription?.actions?.checkStatus) {
        setIsLoadingSubscription(true);
        await new Promise(resolve => setTimeout(resolve, 1500));
        try {
          const result = await subscription.actions.checkStatus();
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 SuccessDisplay: Subscription status result:', result);
          }
          const renewDate = result?.data?.renewsAt || result?.data?.expiresAt || subscription.renewsAt || subscription.expiresAt;
          if (renewDate) {
            setNextPaymentDate(renewDate);
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error('🔍 SuccessDisplay: Error fetching subscription status:', err);
          }
        } finally {
          setIsLoadingSubscription(false);
        }
      } else {
        setIsLoadingSubscription(false);
      }
    };
    fetchSubscriptionStatus();
  }, [sessionId]);
  
  // Update nextPaymentDate when subscription context updates
  useEffect(() => {
    const renewDate = subscription?.renewsAt || subscription?.expiresAt;
    if (renewDate && !nextPaymentDate) {
      setNextPaymentDate(renewDate);
    }
  }, [subscription?.renewsAt, subscription?.expiresAt, nextPaymentDate]);

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return t('stripe.success.nextPaymentValue') || '–';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t('stripe.success.nextPaymentValue') || '–';
      
      return date.toLocaleDateString(language === 'bg' ? 'bg-BG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return t('stripe.success.nextPaymentValue') || '–';
    }
  };

  /**
   * Handle portal button click
   */
  const handlePortal = async () => {
    if (!sessionId) {
      setError(t('stripe.errors.noSessionId') || 'Session ID is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await StripeService.createPortalSession(sessionId);

      if (result.success && result.url) {
        // Redirect to Stripe customer portal
        window.location.href = result.url;
      } else {
        // Handle error
        setError(result.error?.message || t('stripe.errors.portalFailed') || 'Failed to create portal session');
        setLoading(false);
      }
    } catch (err) {
      console.error('Portal error:', err);
      setError(t('stripe.errors.portalFailed') || 'An error occurred while accessing the portal');
      setLoading(false);
    }
  };

  return (
    <section className="stripe-success-hero">
      <div className="stripe-success-hero__badge">
        <span className="stripe-success-hero__check">✔</span>
      </div>

      <h2 className="stripe-success-hero__title">
        {t('stripe.success.heroTitle')}
      </h2>
      <p className="stripe-success-hero__subtitle">
        {t('stripe.success.heroSubtitle')}
      </p>

      <div className="stripe-success-hero__card">
        <h3 className="stripe-success-hero__card-title">
          {t('stripe.success.orderDetails')}
        </h3>
        <div className="stripe-success-hero__row">
          <span className="stripe-success-hero__label">{t('stripe.success.plan')}</span>
          <span className="stripe-success-hero__value">{t('stripe.product.title')}</span>
        </div>
        <div className="stripe-success-hero__row">
          <span className="stripe-success-hero__label">{t('stripe.success.status')}</span>
          <span className="stripe-success-hero__value stripe-success-hero__value--active">
            {t('stripe.success.statusActive')}
          </span>
        </div>
        <div className="stripe-success-hero__row">
          <span className="stripe-success-hero__label">{t('stripe.success.nextPayment')}</span>
          <span className="stripe-success-hero__value">
            {isLoadingSubscription ? (
              <span style={{ opacity: 0.6 }}>...</span>
            ) : (
              formatDate(nextPaymentDate)
            )}
          </span>
        </div>
      </div>

      {error && (
        <div className="stripe-success-hero__error" role="alert">
          {error}
        </div>
      )}

      <div className="stripe-success-hero__actions">
        <Button
          variant="primary"
          size="large"
          onClick={() => navigate('/dashboard')}
          fullWidth
          className="stripe-success-hero__cta"
        >
          {t('stripe.success.gotoSurebets')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={handlePortal}
          loading={loading}
          disabled={loading || !sessionId}
          fullWidth
          className="stripe-success-hero__portal"
        >
          {t('stripe.success.manageBilling')}
        </Button>
      </div>
    </section>
  );
};

export default SuccessDisplay;

